import { getDeviceId } from '@app/config/device_id';
import { getPaygContext, formatPhoneNumber } from '@app/config/util';
import { log, LOG_LEVELS } from '@common/config/app_logger';

import {
  browserContext, paramOrNul, toCamelCase, toSnakeCase, withCkoGetDeviceSessionIdApi, getReferrerUrl
} from '@common/config/utils';
import queryString from 'query-string';

const request = (url, {
  opts = {}, ignoreBaseUrl = false, ignorError404 = false, ignoreUserUpdate = false
} = {}) => {
  const endpoint = ignoreBaseUrl ? url : window.endpoint + url;

  return window.mbwFetch(endpoint, {
    ...opts
  }, { ignorError404, ignoreUserUpdate });
};

export const validateApplePayMerchantApi = (paymentSourceId, paymentSessionUrl) => {
  log('API', 'validateApplePayMerchantApi');
  return request('/my/apple_pay_sessions', {
    opts: {
      method: 'POST',
      body: JSON.stringify(toSnakeCase({
        paymentSourceId,
        paymentSessionUrl
      }))
    }
  });
};

export const loadAdvisorAPI = (slug, params) => {
  log(LOG_LEVELS.INFO, 'API', 'loadAdvisorAPI');
  const queryParameters = queryString.stringify(toSnakeCase(params));
  return request(`/mba_s/advisors/${ slug }?${ queryParameters }`);
};

export const loadAdvisorFeedbacksAPI = (id, params) => {
  const {
    positive, negative
  } = toCamelCase(queryString.parse(window.location.search));
  const queryParameters = queryString.stringify(toSnakeCase({
    ...params, positive, negative
  }));
  return request(`/advisors/${ id }/feedbacks?${ queryParameters }`);
};

export const loadFavoriteAfvisorsAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'loadFavoriteAfvisorsAPI');
  return request('/my/favorite_advisors');
};

export const favoriteAdvisorAPI = (advisorId) => {
  log(LOG_LEVELS.INFO, 'API', 'favoriteAdvisorAPI');
  return request('/mba/my/favorite_advisors', {
    opts:{
      method: 'POST',
      body: JSON.stringify(toSnakeCase({ advisorId }))
    }
  });
};
export const unFavoriteAdvisorAPI = (advisorId) => {
  log(LOG_LEVELS.INFO, 'API', 'unFavoriteAdvisorAPI');
  return request(`/mba/my/favorite_advisors/${ advisorId }`, { opts: { method: 'DELETE' } });
};

export const getPricingOptions = (advisorId, options) => {
  log(LOG_LEVELS.INFO, 'API', 'getPricingOptions');
  const params = queryString.stringify(toSnakeCase(options));
  return request(`/advisors/${ advisorId }/pricing_options?${ params }`);
};

export const createChatApi = (pricePerMinute, duration, advisorId, startChatClickSource, urgencySignalText, homepageSection, fxRate, currency) => {
  log(LOG_LEVELS.INFO, 'API', 'createChatApi');
  const clickSourceData = { clickSource: startChatClickSource, section: homepageSection };
  return request('/my/chats', {
    opts: {
      method: 'POST',
      body: JSON.stringify(toSnakeCase({
        advisorId, duration, pricePerMinute, clickSourceData, urgencySignalText, fxRate, currency
      }))
    }
  });
};

export const loadMyOrdersAPI = (params) => {
  log(LOG_LEVELS.INFO, 'API', 'loadMyOrdersAPI');
  return request(`/mba/my/orders?${ queryString.stringify(params) }`);
};

export const loadAdvisorOrdersAPI = (page, advisorID) => {
  log(LOG_LEVELS.INFO, 'API', 'loadAdvisorOrdersAPI');
  return request(`/my/orders?advisor_id=${ advisorID }&current_page=${ page }`);
};

export const hangupChatApi = (orderId) => {
  log(LOG_LEVELS.INFO, 'API', 'hangupChatApi');
  return request(`/my/orders/${ orderId }`, {
    opts: {
      method: 'DELETE'
    }
  });
};

export const continueChatApi = (orderId, duration) => {
  log(LOG_LEVELS.INFO, 'API', 'continueChatApi');
  return request(`/orders/${ orderId }/chats?duration=${ duration }`, {
    opts: {
      method: 'POST'
    }
  });
};

export const loadOrderDetailsAPI = (orderId) => {
  log(LOG_LEVELS.INFO, 'API', 'loadOrderDetailsAPI');
  return request(`/mba/my/orders/${ orderId }`);
};

export const logInAPI = (email, password, remeberMe, recaptchaToken) => {
  log(LOG_LEVELS.INFO, 'API', 'logInAPI');
  return request('/users/sign_in', {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        user: {
          email,
          password,
          remember_me: remeberMe
        },
        rc_token: recaptchaToken
      })
    },
    ignoreUserUpdate: true
  });
};

export const registerAPI = (email, password, rememberMe, recaptchaToken) => {
  log(LOG_LEVELS.INFO, 'API', 'registerAPI');
  return request('/users', {
    opts:{
      method: 'POST',
      body: JSON.stringify({
        user: {
          email,
          password,
          intention: 'client',
          remember_me: rememberMe,
          ...paramOrNul('referal_url', getReferrerUrl())
        },
        rc_token: recaptchaToken
      })
    },
    ignoreUserUpdate: true
  });
};

export const authSocAPI = ({
  facebookToken, googleToken, rememberMe, consentedToGDPR
}) => {
  log(LOG_LEVELS.INFO, 'API', 'authSocAPI');
  return request('/users', {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        user: {
          facebook_token: facebookToken,
          google_id_token: googleToken,
          remember_me: rememberMe,
          consent_to_privacy_policy: consentedToGDPR,
          intention: 'client'
        }
      })
    },
    ignoreUserUpdate: true
  });
};

export const getCurrentUserAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getCurrentUserAPI');
  const query = queryString.stringify({ with_verification: true });
  return request(`/my/account?${ query }`);
};

export const updateUserAPI = (payload) => request('/my/account/', {
  opts: {
    method: 'PUT',
    body: JSON.stringify(toSnakeCase({ user:{ ...payload } }))
  }
});

export const updateTcpBanner = () => request('/my/account/update_tc_and_pp_version', {
  opts: {
    method: 'PUT'
  }
});

export const checkNickname = nickname => {
  log(LOG_LEVELS.INFO, 'API', 'checkNickname');
  const queryParameters = queryString.stringify({ nickname });
  return request(`/my/account/check?${ queryParameters }`);
};

export const introduceYourselfUpdate = ({ nickname, gender, date }) => {
  log(LOG_LEVELS.INFO, 'API', 'introduceYourselfUpdate');
  return request('/my/account/', {
    opts: {
      method: 'PUT',
      body: JSON.stringify({ nickname, gender, date_of_birth: date })
    }
  });
};

export const sendOrderReviewAPI = (feedback, orderId) => {
  log(LOG_LEVELS.INFO, 'API', 'sendOrderReviewAPI');
  return request(`/my/orders/${ orderId }`, {
    opts: {
      method: 'PUT',
      body: JSON.stringify(toSnakeCase(feedback))
    }
  });
};

export const deleteOrderAPI = (orderId) => {
  log(LOG_LEVELS.INFO, 'API', 'deleteOrderAPI');
  return request(`/my/orders/${ orderId }`, {
    opts: {
      method: 'PUT',
      body: JSON.stringify({ delete: true })
    }
  });
};

export const passwordUpdateAPI = (password, confirmPassword) => {
  log(LOG_LEVELS.INFO, 'API', 'passwordUpdateAPI');
  return request('/my/account', {
    opts:{
      method: 'PUT',
      body: JSON.stringify({ user: { password, password_confirmation: confirmPassword } })
    }
  });
};

export const loadNotificationPreferencesAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'loadNotificationPreferencesAPI');
  return request('/my/notification_preferences');
};

export const updateNotificationPreferencesAPI = (preferences) => {
  log(LOG_LEVELS.INFO, 'API', 'updateNotificationPreferencesAPI');
  return request('/my/notification_preferences', {
    opts: {
      method: 'PUT',
      body: JSON.stringify(toSnakeCase(preferences))
    }
  });
};

export const logOutAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'logOutAPI');
  return request('/users/logout/', {
    opts: {
      method: 'DELETE'
    }
  });
};

export const getCreditsAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getCreditsAPI');
  const queryParameters = queryString.stringify({
    with_payment_sources: true, ...browserContext
  });
  return request(`/my/app_credits?${ queryParameters }`);
};

export const createNotifyMeAPI = (advisorId, additionParams) =>  {
  log(LOG_LEVELS.INFO, 'API', 'postNotifyMeAPI');
  return request(`/mba/advisors/${ advisorId }/availability_notification`, {
    opts: {
      method: 'POST',
      body: JSON.stringify(toSnakeCase(additionParams))
    }
  });
};

export const deleteNotifyMeAPI = (advisorId) =>  {
  log(LOG_LEVELS.INFO, 'API', 'postNotifyMeAPI');
  return request(`/mba/advisors/${ advisorId }/availability_notification`, {
    opts: {
      method: 'DELETE'
    }
  });
};

export const promoCodeAPI = (code, token) => {
  log(LOG_LEVELS.INFO, 'API', 'promoCodeAPI');
  return request('/my/promo_code_redemptions', {
    opts:{
      method: 'POST',
      body: JSON.stringify({ code, rc_token: token })
    }
  });
};

export const passwordForgotAPI = (email) => {
  log(LOG_LEVELS.INFO, 'API', 'passwordForgotAPI');
  return request('/users/password', {
    opts: {
      method: 'POST',
      body: JSON.stringify({ user: { email } })
    }
  });
};

export const loadTranscriptAPI = (orderId) => {
  log(LOG_LEVELS.INFO, 'API', 'loadTranscriptAPI');
  return request(`/my/orders/${ orderId }/transcript`);
};

export const loadCashbackStatusAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'loadCashbackStatusAPI');
  return request('/my/cashbacks/new');
};

export const sendPNTokenToServerAPI = (token) => {
  log(LOG_LEVELS.INFO, 'API', 'sendPNTokenToServerAPI');

  return request('/my/account', {
    opts: {
      method: 'PUT',
      body: JSON.stringify({ user: { web_push_token: token } })
    }
  });
};

export const changeAppStateAPI = (orderId, state) => {
  log(LOG_LEVELS.INFO, 'API', 'changeAppStateAPI');
  return request(`/my/orders/${ orderId }/client_state?state=${ state }`, {
    opts:{
      method: 'PUT',
      keepalive: true
    }
  });
};

export const loadAvailablePaymentSourcesApi = (isMyAccountPage = null) => {
  log(LOG_LEVELS.INFO, 'API', 'loadAvailablePaymentSourcesApi');
  let queryParameters = queryString.stringify({ ...browserContext, ...getPaygContext() });
  if (isMyAccountPage) {
    queryParameters = { ...queryParameters, is_my_account_page: isMyAccountPage };
  }
  return request(`/my/payment_sources?${ queryParameters }`);
};

export const removePaymentSourceApi = (id) => {
  log(LOG_LEVELS.INFO, 'API', 'removePaymentSourceApi');
  return request(`/my/payment_sources/${ id }`, {
    opts:{
      method: 'DELETE'
    }
  });
};

export const addPaymentSourceApi = (method) => {
  log(LOG_LEVELS.INFO, 'API', 'addPaymentSourceApi');
  return request('/my/payment_sources', {
    opts: {
      method: 'POST',
      body: JSON.stringify({ method, ...browserContext })
    }
  });
};

export const loadPaymentOptionsApi = (token, isMyAccountPage = null) => {
  log(LOG_LEVELS.INFO, 'API', 'loadPaymentOptionsApi');
  return request('/my/payment_sources/current', {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        ...browserContext, ...getPaygContext(), rc_token: token, is_my_account_page: isMyAccountPage
      })
    }
  });
};

export const editPaymentSourceApi = ({ id, params, type }) => {
  const parameters = JSON.stringify({ ...params, ...browserContext });
  log(LOG_LEVELS.INFO, 'API', `${ type }`);
  return request(`/my/payment_sources/${ id }`, {
    opts: {
      method: 'PUT',
      body: parameters
    }
  });
};

export const getAvatarPresignedURL = () => {
  log(LOG_LEVELS.INFO, 'API', 'getAvatarPresignedURL');
  return request('/my/account/upload_configs?asset_type=avatar');
};

export const voipStartCallAPI = (advisorId, duration, ppm, type, tryout, startChatClickSource, urgencySignalText, homepageSection, fxRate, currency) => {
  log(LOG_LEVELS.INFO, 'API', 'voipStartCallAPI', duration, ppm, type, tryout, startChatClickSource, urgencySignalText, homepageSection);
  const clickSourceData = { clickSource: startChatClickSource, section: homepageSection };
  return request('/my/conversations', {
    opts: {
      method: 'POST',
      body: JSON.stringify(toSnakeCase({
        advisorId,
        duration,
        pricePerMinute: ppm,
        conversationType: type,
        tryout,
        clickSourceData,
        urgencySignalText,
        fxRate,
        currency
      }))
    }
  });
};

export const voipContinueCallAPI = (orderId, duration, ppm) => {
  log(LOG_LEVELS.INFO, 'API', 'voipContinueCallAPI', orderId, duration, ppm);
  return request(`/orders/${ orderId }/conversations`, {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        duration,
        price_per_minute: ppm
      })
    }
  });
};

export const voipNotifyPeerConnectedAPI = (orderId, conversationId) => {
  log(LOG_LEVELS.INFO, 'API', 'voipNotifyPeerConnectedAPI', orderId, conversationId);
  return request(`/orders/${ orderId }/conversations/${ conversationId }/peers`, {
    opts: {
      method: 'PUT'
    }
  });
};

export const voipPullConversationAPI = (orderId, conversationId) => {
  log(LOG_LEVELS.INFO, 'API', 'voipPullConversationAPI', orderId, conversationId);
  return request(`/orders/${ orderId }/conversations/${ conversationId }`, {
    opts: {
      method: 'PUT'
    }
  });
};

export const voipTimerEndConversationAPI = (orderId, conversationId) => {
  log(LOG_LEVELS.INFO, 'API', 'voipTimerEndConversationAPI', orderId, conversationId);
  return request(`/orders/${ orderId }/conversations/${ conversationId }/timer`, {
    opts: {
      method: 'DELETE'
    }
  });
};

export const voipHangupConversationAPI = (orderId, conversationId) => {
  log(LOG_LEVELS.INFO, 'API', 'voipHangupConversationAPI', orderId, conversationId);
  return request(`/orders/${ orderId }/conversations/${ conversationId }`, {
    opts: {
      method: 'DELETE'
    }
  });
};

export const voipForceHangupConversationAPI = (orderId, conversationId) => {
  log(LOG_LEVELS.INFO, 'API', 'voipForceHangupConversationAPI', orderId, conversationId);
  return request(`/orders/${ orderId }/conversations/${ conversationId }/force`, {
    opts: {
      method: 'DELETE'
    }
  });
};

export const voipPingAPI = (url, buyerDuration, advisorDuration, sequenceNumber) => {
  log(LOG_LEVELS.INFO, 'API', 'voipPingAPI', url, buyerDuration, advisorDuration, sequenceNumber);
  return request(`${ url }?duration_buyer=${ buyerDuration }&duration_advisor=${ advisorDuration }&sequence_number=${ sequenceNumber }`, {
    opts: {
      method: 'POST'
    },
    ignoreBaseUrl: true
  });
};

export const getAdditionalPurchaseInfoFromURL = (URL) => {
  log(LOG_LEVELS.INFO, 'API', `getAdditionalPurchaseInfoFromURL ${ URL }`);
  return request(URL, { ignoreUserUpdate: true });
};

export const getUserVerificationsAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getUserVerificationsAPI');
  return request('/my/user_verifications');
};

export const createUserVerificationAPI = (verification) => {
  log(LOG_LEVELS.INFO, 'API', 'createUserVerificationAPI');
  return request('/my/user_verifications', { opts: { method: 'POST', body: JSON.stringify(verification) } });
};

export const getVerificationStatusAPI = (id) => {
  log(LOG_LEVELS.INFO, 'API', `getVerificationStatus for id: ${ id }`);
  return request(`/my/user_verifications/${ id }`);
};

export const jumioUploadedAPI = (verification) => {
  const { id } = verification;
  log(LOG_LEVELS.INFO, 'API', `jumioUploadedAPI for id: ${ id }`);
  return request(`/my/user_verifications/${ id }`, { opts: { method:'PUT', body: JSON.stringify(verification) } });
};

export const getHomePage = (params, partial) => {
  log(LOG_LEVELS.INFO, 'API', 'getHomePage');
  const queryParameters = queryString.stringify({ ...toSnakeCase(params), partial });

  // return request(`/garden/home?${ queryParameters }`);
  return request(`/mba/home?${ queryParameters }`);
};

export const getSearchList = (params) => {
  const {
    includeOffline, minNumberOfReviewsGe, ppmGe, ppmLe, readingVia, query, localCurrency
  } = toCamelCase(queryString.parse(window.location.search));
  const queryParameters = queryString.stringify(toSnakeCase({
    ...params, includeOffline, minNumberOfReviewsGe, ppmGe, ppmLe, readingVia, query, localCurrency
  }));
  return request(`/mba/advisors?${ queryParameters }`);
};

export const getCreditCardProcessingDataApi = ({ url, params }) => {
  const queryParameters = queryString.stringify({ ...params });
  log(LOG_LEVELS.INFO, 'API', 'getCreditCardProcessingDataApi');
  return request(`${ url }${ queryParameters }`, { ignoreBaseUrl: true });
};

const createCreditCardProcessingPurchaseRequest = (params) => {
  const parameters = JSON.stringify(toSnakeCase({ ...params }));
  log(LOG_LEVELS.INFO, 'API', 'createCreditCardProcessingPurchaseRequest');
  return request('/my/user_credits', {
    opts: {
      method: 'POST',
      body: parameters
    }
  });
};

export const createCreditCardProcessingPurchaseApi = (params) => {
  log(LOG_LEVELS.INFO, 'API', 'createCreditCardProcessingPurchaseApi');
  return withCkoGetDeviceSessionIdApi({ callback: createCreditCardProcessingPurchaseRequest, params });
};

export const getCreditCardProcessingPurchaseResponceApi = (id, { platform }) => {
  log(LOG_LEVELS.INFO, 'API', `getCreditCardProcessingPurchaseResponce ID: ${ id }`);
  const queryParameters = queryString.stringify(toSnakeCase({ platform }));
  return request(`/my/user_credits/${ id }?${ queryParameters }`, { ignoreUserUpdate: true });
};
export const getDirectMessagesApi = (id) => {
  log(LOG_LEVELS.INFO, 'API', `getDirectMessagesApi ID: ${ id }`);
  const queryParameters = queryString.stringify(toSnakeCase({ openFrom: 'advisor_dm_email' }));
  return request(`/direct_messages/${ id }?${ queryParameters }`, { ignorError404: true });
};

export const getSEODataAPI = (route) => {
  log(LOG_LEVELS.INFO, 'API', `getSEOData for ${ route }`);
  return request(`/seo_data${ route }`, { ignorError404: true });
};

export const getFeesApi = ({ amountToPay }) => {
  log(LOG_LEVELS.INFO, 'API', `getFees for ${ amountToPay }`);
  const query = queryString.stringify({ amount_to_pay: amountToPay });
  return request(`/my/app_credits/calculate_fees?${ query }`);
};

export const getActivityApi = (advisorId, filter, paginationParams) => {
  log(LOG_LEVELS.INFO, 'API', 'getActivityApi', advisorId, filter, paginationParams);
  const query = queryString.stringify({ ...paginationParams });
  return request(`/my/activity?advisor_id=${ advisorId }&show_only=${ filter }&${ query }`);
};

export const createActivityMessage = (advisorId, message) => {
  log(LOG_LEVELS.INFO, 'API', 'createActivityMessage', advisorId);
  return request(`/advisors/${ advisorId }/feed_messages`, {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        message_kind: message,
        content: {
          message
        }
      })
    }
  });
};

const validateCreditCardRequest = (params) => {
  const parameters = JSON.stringify(toSnakeCase({ ...params }));
  log(LOG_LEVELS.INFO, 'API', 'validateCreditCardRequest');
  return request('/my/payment_validations', {
    opts: {
      method: 'POST',
      body: parameters
    }
  });
};

export const validateCreditCardApi = ({ requestParams }) => {
  log(LOG_LEVELS.INFO, 'API', 'validateCreditCardApi');
  return withCkoGetDeviceSessionIdApi({ callback: validateCreditCardRequest, params: requestParams });
};

export const getValidateCreditCardStatusApi = ({ id }) => {
  log(LOG_LEVELS.INFO, 'API', 'getValidateCreditCardStatusApi');
  return request(`/my/payment_validations/${ id }`);
};

export const getMyAccountCreditsApi = (sequence) => {
  log(LOG_LEVELS.INFO, 'API', 'getMyAccountCreditsApi');
  return request(`/my/account/credits?sequence=${ sequence }`);
};

export const getCategoriesAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'loadCategoriesAPI');
  return request('/categories');
};

export const welcomePromoCodesAPI  = (deeplink, token) => {
  log('API', 'welcomePromoCodesAPI');
  return request('/welcome_promo_codes ', {
    opts:{
      method: 'POST',
      body: JSON.stringify({
        deeplink,
        rc_token: token
      })
    }
  });
};

export const getSimilarOnlineAdvisorsApi = (id) => {
  log('API', 'getSimilarOnlineAdvisorsApi');
  return request(`/mba/similar_online_advisors?advisor_id=${ id }`);
};

export const getMyAccountBadgesApi = ({ advisorId }) => {
  log('API', 'getMyAccountBadgesApi');
  const query = queryString.stringify({ advisor_id: advisorId });
  return request(`/my/account/badges?${ query }`);
};

export const advisorTipsBonusesSubmitApi  = ({ orderId, params }) => {
  const parameters = JSON.stringify({ ...params });
  log(LOG_LEVELS.INFO, 'API', `advisorTipsBonusesSubmitApi, OrderId: ${ orderId }`);
  return request(`/my/orders/${ orderId }/bonuses`, {
    opts:{
      method: 'POST',
      body: parameters
    }
  });
};

export const getAdvisorBonusApi = (orderId) => {
  log('API', 'getAdvisorBonusApi');
  return request(`/my/orders/${ orderId }/bonuses`);
};

export const processTopup = (orderId) => {
  log('API', 'processTopup');
  return request(`/orders/${ orderId }/chats/topup`, {
    opts: {
      method: 'POST'
    }
  });
};

export const firstVisitEventsApi = async () => {
  const deviceId = await getDeviceId();
  const params = {};
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  log('API', 'firstVisitEventsApi');
  return request('/first_visit_events', {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        device_id: deviceId,
        url: window.location.href,
        ...params
      })
    }
  });
};

export const getClientSettingsApi = async () => {
  log('API', 'getClientSettingsApi');
  return request('/client_settings/web');
};

export const getAiSearchConfigApi = async () => {
  log('API', 'getAiSearchConfigApi');
  return request('/api/ai_searches/new');
};

export const getBlockedAdvisors = (query, page) => {
  log('API', 'getBlockedAdvisors');
  return request(`/my/user/blocked_interactions/blocked_advisors?${ queryString.stringify({
    search_text: query,
    page
  }) }`);
};

export const getBlockReasons = (advisorId) => {
  log('API', 'getBlockReasons');
  return request(`/my/user/blocked_interactions?advisor_id=${ advisorId }`);
};

export const blockAdvisor = (advisorId, reason, custom) => {
  const parameters = JSON.stringify({ advisor_id: advisorId, reason, ...paramOrNul('custom', custom) });
  log('API', 'getBlockReasons');
  return request('/my/user/blocked_interactions', {
    opts: {
      method: 'POST',
      body: parameters
    }
  });
};

export const unblockAdvisor = (advisorId) => {
  log('API', 'getBlockReasons');
  return request(`/my/user/blocked_interactions/${ advisorId }`, {
    opts: {
      method: 'DELETE'
    }
  });
};

export const createPaymentSessionApi = (paymentSourceId, userCreditId, rcToken) => {
  log('API', 'createPaymentSessionApi');
  return request(`/my/payment_sources/${ paymentSourceId }/payment_sessions`, {
    opts: {
      method: 'POST',
      body: JSON.stringify({ user_credit_id: userCreditId, rc_token: rcToken })
    }
  });
};

export const processPaymentSessionPaymentApi = (paymentSourceId, paymentId, userCreditId, paymentSessionId) => {
  log('API', 'processPaymentSessionPayment');
  return request(`/my/payment_sources/${ paymentSourceId }/payment_sessions/${ paymentSessionId }`, {
    opts: {
      method: 'PUT',
      body: JSON.stringify({ user_credit_id: userCreditId, payment_id: paymentId })
    }
  });
};

export const getGamifiedHubItemsApi = (signal) => request('/mba/spiritual_hub', {
  opts: {
    signal
  }
});

export const getAdvisorsStatuses = (advisorsIds) => {
  log('API', 'getAdvisorsStatuses');
  return request(`/mba/advisors?advisor_ids=${ advisorsIds.join(',') }`);
};

export const getAdvisorAutocompleteAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getAdvisorAutocompleteAPI');
  return request('/mba/advisor_autocomplete');
};

export const getTopicsAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getTopicsAPI');
  return request('/purple_topics');
};

export const getAdvisorsAPI = (paginationParams) => {
  let url = '/pending_web_order/advisors';

  if (paginationParams) {
    const queryParams = Object.entries(paginationParams)
      .map(([key, value]) => `${ encodeURIComponent(key) }=${ encodeURIComponent(value) }`)
      .join('&');

    url += `?${ queryParams }`;
  }

  return request(url);
};

export const createPendingWebOrderAPI = ({ token, ...props }) => {
  const params = JSON.stringify(toSnakeCase({ pendingWebOrder: props, rc_token: token }));
  log(LOG_LEVELS.INFO, 'API', 'createPendingWebOrdersAPI', { orderId: params.orderId });
  return request('/pending_web_order', {
    opts: {
      method: 'POST',
      body: params
    }
  });
};

export const editPendingWebOrderAPI = (props) => {
  const params = JSON.stringify(toSnakeCase({ pendingWebOrder: props }));
  log(LOG_LEVELS.INFO, 'API', 'editPendingWebOrderAPI', { orderId: params.orderId });
  return request('/pending_web_order', {
    opts: {
      method: 'PUT',
      body: params
    }
  });
};

export const pendingWebOrderSubmitPaymentSourceAPI = ({ paymentSourceId, params }) => {
  const parameters = JSON.stringify(params);
  log(LOG_LEVELS.INFO, 'API', 'pendingWebOrderSubmitApi', { orderId: parameters.orderId });
  return request(`/pending_web_order/submit_payment_source/${ paymentSourceId }`, {
    opts: {
      method: 'POST',
      body: parameters
    }
  });
};

export const pendingWebOrderSubmitAPI = () => request('/pending_web_order/submit', {
  opts: { method: 'POST' }
});

export const getPaymentConfigsAPI = (token) => {
  log(LOG_LEVELS.INFO, 'API', 'getPaymentConfigsAPI');
  return request('/pending_web_order/payment_configs', {
    opts: {
      method: 'POST',
      body: JSON.stringify({ rc_token: token })
    }
  });
};

export const validateEmailAPI = (email) => {
  log(LOG_LEVELS.INFO, 'API', 'pendingWebOrderValidateEmail');
  const query = queryString.stringify({ email });
  return request(`/pending_web_order/validate_params?${ query }`);
};

export const validateNicknameAPI = (nickname) => {
  log(LOG_LEVELS.INFO, 'API', 'validateNicknameAPI');
  const query = queryString.stringify({ nickname });
  return request(`/pending_web_order/validate_params?${ query }`);
};

export const loadOrderAPI = (token) => {
  log(LOG_LEVELS.INFO, 'API', 'loadOrderAPI');
  return request(`/pending_web_order/${ token }`);
};

export const onOrderSeenAPI = (token) => {
  log(LOG_LEVELS.INFO, 'API', 'onOrderSeen');
  return request(`/pending_web_order/${ token }/seen`, { opts: { method: 'PUT' } });
};

export const getWebOrderInfoAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getwebOrderInfoAPI');
  return request('/pending_web_order/web_order_data');
};

export const sendPhoneVerificationSmsAPI = (phoneNumber, context = 'auth', recaptchaToken) => {
  log(LOG_LEVELS.INFO, 'API', 'sendPhoneVerificationSmsAPI');
  return request('/mba/users/phone_confirmation_codes', {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        mobile_phone_id: formatPhoneNumber(phoneNumber),
        context,
        rc_token: recaptchaToken
      })
    }
  });
};

export const loginByPhoneAPI = ({
  phoneNumber, confirmationCode, consentedToGDPR, consentToMarketingSms, email = '', signUpFlow = ''
}) => {
  log(LOG_LEVELS.INFO, 'API', 'loginByPhoneAPI');
  return request('/mba/users', {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        user: {
          mobile_phone_id: formatPhoneNumber(phoneNumber),
          confirmation_code: confirmationCode,
          consent_to_privacy_policy: consentedToGDPR,
          consent_to_marketing_sms: consentToMarketingSms,
          email,
          signup_flow: signUpFlow
        }
      })
    }
  });
};

export const addPhoneNumberAPI = ({
  phoneNumber, confirmationCode, consentToMarketingSms, recaptchaToken
}) => {
  log(LOG_LEVELS.INFO, 'API', 'addPhoneNumberAPI');
  return request('/my/phone_number', {
    opts: {
      method: 'POST',
      body: JSON.stringify({
        user: {
          mobile_phone_id: formatPhoneNumber(phoneNumber),
          consent_to_marketing_sms: consentToMarketingSms
        },
        confirmation_code: confirmationCode,
        rc_token: recaptchaToken
      })
    }
  });
};

export const requestPhoneChangeAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'requestPhoneChangeAPI');
  return request('/my/phone_number/request_change', {
    opts: {
      method: 'POST'
    }
  });
};

export const updateMobilePhoneWithTokenAPI = ({
  phoneNumber,
  consentToMarketingSms,
  confirmationCode,
  token
}) => {
  log(LOG_LEVELS.INFO, 'API', 'updateMobilePhoneWithTokenAPI');
  return request('/my/phone_number', {
    opts: {
      method: 'PUT',
      body: JSON.stringify({
        user: {
          mobile_phone_id: formatPhoneNumber(phoneNumber),
          consent_to_marketing_sms: consentToMarketingSms
        },
        confirmation_code: confirmationCode,
        token
      })
    }
  });
};

/**
 * Validates edit phone number token via /validate_entity.
 * Rejects on non-2xx; caller should handle 422 and check response body for operation_status.
 */
export const validateEntityApi = (editPhoneNumberToken) => {
  log(LOG_LEVELS.INFO, 'API', 'validateEntityApi');
  return request('/validate_entity', {
    opts: {
      method: 'POST',
      body: JSON.stringify(toSnakeCase({
        editPhoneNumberToken
      }))
    }
  });
};

export const updateCurrencyAPI = (currency) => {
  log(LOG_LEVELS.INFO, 'API', 'changeCurrencyAPI');
  return request('/currency', {
    opts: {
      method: 'POST',
      body: JSON.stringify({ currency })
    }
  });
};
